import type { BuildingId, FactionId, PlanetKind, SlotLayer, TechId } from './ids';
import type { TradeGoodId } from './market';

/**
 * The planet economy (GDD §5): buildings staffed by the population turn
 * energy and inputs into goods once per economic cycle; the population eats
 * food and uses electronics, and how well those needs are met (satisfaction)
 * scales everything the planet produces. Starting values for tuning.
 */

export interface BuildingCost {
  readonly cash: number;
  readonly alloys: number;
  readonly metals: number;
}

export interface BuildingDef {
  readonly id: BuildingId;
  /** i18n key: `building.<id>.name`. */
  readonly i18nKey: `building.${BuildingId}`;
  /** Layers this building may occupy. */
  readonly layers: readonly SlotLayer[];
  /** At most one per planet. */
  readonly unique: boolean;
  /** Workers per level. */
  readonly jobs: number;
  /** Energy upkeep per level per cycle. */
  readonly energy: number;
  /** Inputs per level per cycle at full output. */
  readonly inputs: Readonly<Partial<Record<TradeGoodId, number>>>;
  /** Goods per level per cycle at full output. */
  readonly outputs: Readonly<Partial<Record<TradeGoodId, number>>>;
  /** Units of the planet's deposit (see PLANET_DEPOSITS) per level per cycle. */
  readonly extracts: number;
  /** Science per level per cycle. */
  readonly science: number;
  /** Nominal energy per level per cycle (power plants). */
  readonly energyOutput: number;
  /** Extra population cap per level. */
  readonly populationCap: number;
  /** Cost of level 1; higher levels cost this × ECONOMY.upgradeCostFactor. */
  readonly cost: BuildingCost;
  /** Construction time of every level, in economic cycles. */
  readonly buildCycles: number;
  /** Technology that unlocks it (#27); null = available from the start. */
  readonly requiresTech: TechId | null;
  /** Defence per level (GDD §8): garrison strength against landings (#24). */
  readonly garrison: number;
  /** Shield capacity per level: absorbs bombardment and crisis strikes (#24, #29). */
  readonly shield: number;
  /** Damage per combat round per level against hostile ships in the capital's hex. */
  readonly guns: number;
}

const building = (
  id: BuildingId,
  def: Partial<Omit<BuildingDef, 'id' | 'i18nKey'>> & Pick<BuildingDef, 'cost'>,
): BuildingDef => ({
  id,
  i18nKey: `building.${id}`,
  layers: ['surface'],
  unique: false,
  jobs: 0,
  energy: 0,
  inputs: {},
  outputs: {},
  extracts: 0,
  science: 0,
  energyOutput: 0,
  populationCap: 0,
  buildCycles: 2,
  requiresTech: null,
  garrison: 0,
  shield: 0,
  guns: 0,
  ...def,
});

export const BUILDINGS: Readonly<Record<BuildingId, BuildingDef>> = {
  spaceport: building('spaceport', {
    layers: ['orbit'],
    unique: true,
    jobs: 2,
    energy: 2,
    cost: { cash: 400, alloys: 20, metals: 0 },
    buildCycles: 4,
  }),
  commerceCenter: building('commerceCenter', {
    unique: true,
    jobs: 2,
    energy: 1,
    cost: { cash: 300, alloys: 10, metals: 0 },
  }),
  researchCenter: building('researchCenter', {
    unique: true,
    jobs: 3,
    energy: 2,
    science: 5,
    cost: { cash: 300, alloys: 10, metals: 0 },
  }),
  agroComplex: building('agroComplex', {
    jobs: 3,
    energy: 2,
    outputs: { food: 8 },
    cost: { cash: 200, alloys: 5, metals: 0 },
  }),
  miningComplex: building('miningComplex', {
    jobs: 3,
    energy: 3,
    extracts: 8,
    cost: { cash: 200, alloys: 5, metals: 0 },
  }),
  smelter: building('smelter', {
    jobs: 3,
    energy: 3,
    inputs: { metals: 6 },
    outputs: { alloys: 4 },
    cost: { cash: 250, alloys: 5, metals: 0 },
  }),
  electronicsFactory: building('electronicsFactory', {
    jobs: 4,
    energy: 4,
    inputs: { metals: 4, exotics: 0.5 },
    outputs: { electronics: 3 },
    cost: { cash: 350, alloys: 10, metals: 0 },
    buildCycles: 3,
  }),
  powerPlant: building('powerPlant', {
    layers: ['surface', 'orbit'],
    jobs: 2,
    energyOutput: 12,
    cost: { cash: 200, alloys: 5, metals: 0 },
  }),
  // A district is built and grown slowly: its levels are the capital's new surface slots.
  housing: building('housing', {
    energy: 1,
    populationCap: 10,
    cost: { cash: 150, alloys: 0, metals: 5 },
  }),
  garrison: building('garrison', {
    jobs: 2,
    energy: 1,
    garrison: 20,
    cost: { cash: 200, alloys: 10, metals: 0 },
  }),
  planetaryShield: building('planetaryShield', {
    layers: ['orbit'],
    unique: true,
    energy: 3,
    shield: 200,
    requiresTech: 'defenseSystems',
    cost: { cash: 300, alloys: 30, metals: 0 },
    buildCycles: 3,
  }),
  groundGuns: building('groundGuns', {
    jobs: 2,
    energy: 2,
    guns: 15,
    requiresTech: 'defenseSystems',
    cost: { cash: 300, alloys: 30, metals: 0 },
    buildCycles: 3,
  }),
};

/** What a mining complex extracts on each kind of home world (GDD §5 «Ресурсы»). */
export const PLANET_DEPOSITS: Readonly<Record<PlanetKind, TradeGoodId>> = {
  lava: 'metals',
  desert: 'metals',
  terran: 'metals',
  asteroids: 'metals',
  gasGiant: 'helium3',
  ice: 'helium3',
};

/** How a faction's population and industry differ (GDD §4, §5). */
export interface FactionEconomy {
  /** Multiplier on food eaten per person. */
  readonly foodFactor: number;
  /** Weights of food and electronics in satisfaction; they sum to 1. */
  readonly needWeights: { readonly food: number; readonly electronics: number };
  /** Multiplier on every building's output. */
  readonly productionFactor: number;
  /** Extra multiplier on output of the faction's cheap good. */
  readonly cheapGoodFactor: number;
}

export const FACTION_ECONOMY: Readonly<Record<FactionId, FactionEconomy>> = {
  earthborn: {
    foodFactor: 1,
    needWeights: { food: 0.75, electronics: 0.25 },
    productionFactor: 1,
    cheapGoodFactor: 1.5,
  },
  // Eat half as much, but implants make electronics their main need;
  // production −25% is the price of the raider path (GDD §4).
  reforged: {
    foodFactor: 0.5,
    needWeights: { food: 0.4, electronics: 0.6 },
    productionFactor: 0.75,
    cheapGoodFactor: 1.5,
  },
  drifters: {
    foodFactor: 1,
    needWeights: { food: 0.75, electronics: 0.25 },
    productionFactor: 1,
    cheapGoodFactor: 1.5,
  },
  skyborn: {
    foodFactor: 1,
    needWeights: { food: 0.75, electronics: 0.25 },
    productionFactor: 1,
    cheapGoodFactor: 1.5,
  },
};

export const ECONOMY = {
  startPopulation: 10,
  /** Population cap of a planet before housing. */
  basePopulationCap: 10,
  /** Population never drops below this: a capital is never empty. */
  minPopulation: 1,
  /**
   * Surface slots (#79, author's decision 20.10.2026): `start` at the
   * outset, up to `max`. New ones are earned, not grown: the best housing
   * district adds `byHousingLevel[level - 1]`, and technologies add theirs.
   */
  surfaceSlots: { start: 4, max: 8, byHousingLevel: [0, 2, 3] },
  orbitSlots: 2,
  /** Consumption per person per cycle. */
  foodPerPerson: 0.5,
  electronicsPerPerson: 0.1,
  /** Satisfaction 0..1 maps linearly onto this output multiplier (GDD §5). */
  satisfactionOutput: { min: 0.6, max: 1.2 },
  /** Growth per cycle when fully fed and below the cap: rate × population, at least `minGrowth`. */
  growthRate: 0.05,
  minGrowth: 0.2,
  /** Hungry cycles before the population starts to shrink. */
  starvationGraceCycles: 2,
  /** Share of the population lost per cycle at zero food (scaled by the shortfall). */
  starvationDeclineRate: 0.1,
  /** Taxes per person per cycle, in the player's currency. */
  taxPerEmployed: 6,
  taxPerUnemployed: 1,
  /** Helium-3 a reactor burns per unit of energy it makes up where solar falls short. */
  heliumPerEnergy: 0.2,
  /** Solar output = nominal × min(1, flux), flux relative to Earth (1 AU from the Sun). */
  maxSolarFactor: 1,
  maxBuildingLevel: 3,
  /** Cost multiplier to reach each level. */
  upgradeCostFactor: { 1: 1, 2: 1.5, 3: 2.5 },
  /** Output history kept for currency rates (GDD §6: last 5 cycles). */
  outputHistoryCycles: 5,
} as const;

export type BuildingLevel = keyof typeof ECONOMY.upgradeCostFactor;

/** Buildings a capital starts with (GDD §5: spaceport in orbit from the start). */
export interface StartingBuilding {
  readonly id: BuildingId;
  readonly layer: SlotLayer;
}
